import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  ListChecks, 
  Search,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '',
  description: '',
  component_ids: [],
  active: true
};

export const Specs = () => {
  const [specs, setSpecs] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSpec, setEditingSpec] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [specsRes, componentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/specs`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/components`, getAuthHeaders())
      ]);
      setSpecs(specsRes.data);
      setComponents(componentsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleComponentToggle = (componentId) => {
    setFormData(prev => ({
      ...prev,
      component_ids: prev.component_ids.includes(componentId)
        ? prev.component_ids.filter(id => id !== componentId)
        : [...prev.component_ids, componentId]
    }));
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingSpec(null);
    setError('');
    setDropdownOpen(false);
    setShowModal(true);
  };

  const openEditModal = (spec) => {
    setFormData({
      name: spec.name || '',
      description: spec.description || '',
      component_ids: spec.component_ids || [],
      active: spec.active ?? true
    });
    setEditingSpec(spec);
    setError('');
    setDropdownOpen(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Spec name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingSpec) {
        await axios.put(
          `${BACKEND_URL}/api/specs/${editingSpec.id}`,
          formData,
          getAuthHeaders()
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/specs`,
          formData,
          getAuthHeaders()
        );
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save spec');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (specId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/specs/${specId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete spec');
    }
  };

  const filteredSpecs = specs.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSelectedComponentNames = () => {
    return components
      .filter(c => formData.component_ids.includes(c.id))
      .map(c => c.name);
  };

  return (
    <div data-testid="specs-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Specs</h1>
          <p className="text-slate-600 mt-1">Manage product specifications</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-spec-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Spec
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search specs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Specs Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredSpecs.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <ListChecks className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No specs yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first spec</p>
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Spec
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-5 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Description</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Components</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredSpecs.map((spec) => (
              <div
                key={spec.id}
                data-testid={`spec-row-${spec.id}`}
                className="grid grid-cols-1 md:grid-cols-5 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="font-medium text-slate-900">{spec.name}</div>
                <div className="text-slate-600 text-sm truncate">{spec.description || '-'}</div>
                <div className="flex flex-wrap gap-1">
                  {spec.component_names?.length > 0 ? (
                    spec.component_names.map((name, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded"
                      >
                        {name}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-sm">-</span>
                  )}
                </div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    spec.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {spec.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {spec.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(spec)}
                    data-testid={`edit-btn-${spec.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(spec.id)}
                    data-testid={`delete-btn-${spec.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this spec? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingSpec ? 'Edit Spec' : 'Add New Spec'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    data-testid="input-description"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Associated Components - Multi Select */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Associated Components
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      data-testid="input-components"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-left flex items-center justify-between bg-white"
                    >
                      <span className={formData.component_ids.length > 0 ? 'text-slate-900' : 'text-slate-400'}>
                        {formData.component_ids.length > 0
                          ? `${formData.component_ids.length} component(s) selected`
                          : 'Select components'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {dropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                        {components.filter(c => c.active).length === 0 ? (
                          <div className="px-3 py-2 text-slate-400 text-sm">No components available</div>
                        ) : (
                          components.filter(c => c.active).map(component => (
                            <label
                              key={component.id}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.component_ids.includes(component.id)}
                                onChange={() => handleComponentToggle(component.id)}
                                className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                              />
                              <span className="text-slate-700">{component.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected Components Tags */}
                  {formData.component_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {getSelectedComponentNames().map((name, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded"
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => {
                              const component = components.find(c => c.name === name);
                              if (component) handleComponentToggle(component.id);
                            }}
                            className="hover:text-blue-900"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    data-testid="input-active"
                    className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingSpec ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
